import React, { Fragment, useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  HStack,
  Checkbox,
  TableContainer,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  SortingState,
  getSortedRowModel,
  getExpandedRowModel,
  ColumnDef,
  Row,
} from "@tanstack/react-table";
import isEmpty from "lodash.isempty";
import { SortUpIcon, SortDownIcon } from "@root/assets/icons";
import { testId as testIdFn } from "src/utils/e2e";
import { Paginate } from "../pagination";
import NoRecordsText from "../common/text/noRecordsText";

type DataTableProps<Data extends object> = {
  data: TObject[];
  columns: ColumnDef<Data, TObject>[];
  renderSubComponent?: (props: { row: Row<Data> }) => React.ReactElement;
  getRowCanExpand?: (row: Row<Data>) => boolean;
  getRowSelected?: (row: Row<Data>) => boolean;
  onRowSelectionChange?: (row: Row<Data>) => boolean;
  onRowSelectionChangeHandler?: (row: TObject) => void;
  pageCount?: TObject;
  page?: TObject;
  pageSize?: TObject;
  handlePageChange?: TObject;
  testId?: string;
  getRowId?: TObject;
  centeredCols?: Array<string>;
  canEditRow?: boolean;
  showPagination?: boolean;
  headerColors?: TObject;
  isRtl?: boolean;
  showFooter?: boolean;
  includeCheckedBoxColumn?: boolean;
  resetCheckBoxes?: boolean;
  onSelectRowThroughCheckbox?: any;
  getAllCheckedData?: boolean;
  selectedData?: TArrayOfObjects;
  isDisabledCheckBox?: boolean;
  defaultSortState?: TObject[];
  theadBg?: string;
  rowHoverBg?: string;
  onRowClick?: (row: any) => void;
  isCourseTable?: boolean;
  courseTableLastRowButton?: React.ReactNode;
  border?: string;
};

export function DataTable<Data extends object>({
  data,
  columns,
  getRowCanExpand,
  renderSubComponent,
  page,
  pageSize,
  pageCount,
  handlePageChange,
  testId,
  getRowId,
  showPagination = true,
  onRowSelectionChangeHandler,
  centeredCols = [],
  includeCheckedBoxColumn = false,
  canEditRow = true,
  headerColors = {},
  isRtl = false,
  showFooter = false,
  resetCheckBoxes = false,
  getAllCheckedData = false,
  onSelectRowThroughCheckbox,
  selectedData,
  isDisabledCheckBox = false,
  defaultSortState = [],
  theadBg,
  rowHoverBg,
  onRowClick,
  isCourseTable = false,
  courseTableLastRowButton,
  border,
}: DataTableProps<Data>) {
  const [t] = useTranslation("common");
  const [sorting, setSorting] = React.useState<SortingState>(defaultSortState);

  const [expanded, setExpanded] = useState({});
  const [allChecked, setAllChecked] = useState(false);

  const [rowSelection, setRowSelection] = useState<{ [key: string]: boolean }>(
    {}
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    onRowSelectionChange: canEditRow ? setRowSelection : undefined,
    getRowCanExpand,
    state: {
      sorting,
      expanded,
      rowSelection,
    },
    getSubRows: () => undefined,
    getRowId,
  });

  useEffect(() => {
    if (!isEmpty(rowSelection) && onRowSelectionChangeHandler) {
      onRowSelectionChangeHandler(
        table.getSelectedRowModel().flatRows[0]?.original
      );
      !includeCheckedBoxColumn && setRowSelection({});
    }
  }, [
    onRowSelectionChangeHandler,
    rowSelection,
    table,
    includeCheckedBoxColumn,
  ]);

  useEffect(() => {
    if (includeCheckedBoxColumn && !getAllCheckedData) {
      const updatedRowSelection: { [key: string]: boolean } = {};
      table.getRowModel().rows.map((row: any) => {
        updatedRowSelection[row.original.id] = false;

        if (onSelectRowThroughCheckbox && row?.original) {
          onSelectRowThroughCheckbox(row?.original, false);
        }
      });

      setRowSelection(updatedRowSelection);

      setAllChecked(false);
    }
  }, [includeCheckedBoxColumn, page, table.getRowModel().rows]);

  useEffect(() => {
    if (resetCheckBoxes && includeCheckedBoxColumn) {
      setRowSelection((prevRowSelection) => {
        const updatedRowSelection: { [key: string]: boolean } = {};

        Object.keys(prevRowSelection).forEach((rowId) => {
          updatedRowSelection[rowId] = false;
        });

        return updatedRowSelection;
      });
    }
  }, [resetCheckBoxes]);

  const sendOriginalRow = (rowId: any, check: boolean) => {
    const originalRow = table
      .getRowModel()
      .rows.find((row: TObject) => row.original?.id === rowId);

    if (onSelectRowThroughCheckbox && originalRow) {
      onSelectRowThroughCheckbox(originalRow?.original, check);
    }
  };

  const sendAllDataOnSelectAll = (select: boolean) => {
    const originalRow: any = table
      .getRowModel()
      .rows.map((item: TObject) => ({ ...item.original, select }));

    if (onSelectRowThroughCheckbox && originalRow) {
      onSelectRowThroughCheckbox(originalRow, select);
    }
  };

  const setSelectionData = (sData: TArrayOfObjects) => {
    const finalSelected: TObject = {};
    const original = table.getRowModel().rows;

    original.forEach((row: TObject) => {
      const { id } = row.original;
      const ifExist = sData?.find(
        (info: any) => info.id === id && info?.select
      );

      finalSelected[id] = ifExist?.select || false;
    });

    const allSelected = Object.values(finalSelected)?.every(
      (item: any) => item
    );

    if (allSelected) setAllChecked(true);
    else setAllChecked(false);

    setRowSelection((prev: any) => ({ ...prev, ...finalSelected }));
  };

  const handleSelectAll = (event: TObject) => {
    const { checked } = event.target;

    setAllChecked(checked);

    setRowSelection((prevRowSelection) => {
      const updatedRowSelection: { [key: string]: boolean } = {};

      Object.keys(prevRowSelection).forEach((rowId) => {
        updatedRowSelection[rowId] = checked;

        if (prevRowSelection[rowId] !== updatedRowSelection[rowId]) {
          sendOriginalRow(rowId, checked);
        }
      });

      return updatedRowSelection;
    });

    sendAllDataOnSelectAll(checked);
  };

  // For Search and Select
  useEffect(() => {
    data?.length && selectedData?.length && setSelectionData(selectedData);
    data?.length &&
      !selectedData?.length &&
      Object.keys(rowSelection).length !== 0 &&
      setRowSelection({});
  }, [
    includeCheckedBoxColumn,
    selectedData?.length,
    data?.length,
    selectedData,
  ]);

  const sendupdatedDataOnSelectOne = (selectedItems: TObject) => {
    const originalRow: any = table.getRowModel().rows.map((rData: any) => ({
      ...rData?.original,
      select: selectedItems[rData?.original?.id],
    }));

    const isAllSelected = originalRow?.every((item: any) => item.select);

    if (onSelectRowThroughCheckbox && originalRow) {
      onSelectRowThroughCheckbox(originalRow, isAllSelected);
    }
  };

  useEffect(() => {
    if (includeCheckedBoxColumn && !getAllCheckedData) {
      setRowSelection((prevRowSelection) => {
        const updatedRowSelection: { [key: string]: boolean } = {};

        Object.keys(prevRowSelection).forEach((rowId) => {
          updatedRowSelection[rowId] = allChecked;

          if (prevRowSelection[rowId] !== updatedRowSelection[rowId]) {
            sendOriginalRow(rowId, allChecked);
          }
        });

        return updatedRowSelection;
      });
    }
  }, [includeCheckedBoxColumn, allChecked, getAllCheckedData]);

  return (
    <Box width="100%" overflowX="auto">
      <TableContainer 
        border={border || "1px solid #CDD6DC"} 
        borderRadius={16}
        maxW="100%"
        overflowX="auto"
      >
        <Table size="lg" overflow={"hidden"} layout="auto">
          <Thead bg={"#f3f1ff"} zIndex={5}>
            {table?.getHeaderGroups()?.map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {includeCheckedBoxColumn && (
                  <Th borderTopStartRadius="8px">
                    <Checkbox
                      isDisabled={isDisabledCheckBox}
                      background={allChecked ? "blue" : "white"}
                      borderColor={allChecked ? "blue" : "white"}
                      colorScheme="white"
                      isChecked={allChecked}
                      onChange={(event) => handleSelectAll(event)}
                      {...testIdFn(`data-table-header-checkbox-${data[0]?.id}`)}
                    />
                  </Th>
                )}
                {headerGroup.headers.map((header, i) => {
                  const { meta }: TObject = header.column.columnDef;

                  const headerTextAlignment = () => {
                    if (centeredCols.includes(header.column.id))
                      return "center";
                  };

                  return (
                    <Th
                      px={"20px"}
                      borderTopStartRadius={
                        i === 0 && !includeCheckedBoxColumn ? "8px" : "0"
                      }
                      borderTopEndRadius={
                        i === headerGroup.headers.length - 1 ? "8px" : "0"
                      }
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      isNumeric={meta?.isNumeric}
                      color={"#535862"}
                      textTransform="capitalize"
                      textAlign={headerTextAlignment()}
                      fontSize={"12px"}
                      fontWeight={500}
                      whiteSpace="normal"
                      wordBreak="break-word"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      <span>
                        {header.column.getIsSorted() ||
                        header.column.columnDef.enableSorting ? (
                          header.column.getIsSorted() === "desc" ? (
                            <SortDownIcon
                              ml="3"
                              aria-label="sorted descending"
                            />
                          ) : (
                            <SortUpIcon ml="3" aria-label="sorted ascending" />
                          )
                        ) : null}
                      </span>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.length &&
            table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row: any) => (
                <Fragment key={row.id}>
                  <Tr
                    {...testIdFn(`data-table-row-${row?.original?.id}`)}
                    key={row.id}
                    _hover={{
                      backgroundColor: "gray.50",
                    }}
                    bg={"bg.theme.gray"}
                    onClick={
                      onRowClick ? () => onRowClick(row.original) : undefined
                    }
                  >
                    {includeCheckedBoxColumn && (
                      <Td p={0}>
                        <Checkbox
                          isDisabled={isDisabledCheckBox}
                          colorScheme="brand"
                          bg="bg.theme.gray"
                          {...testIdFn(
                            `data-table-row-checkbox-${row?.original?.id}`
                          )}
                          isChecked={rowSelection[row.original.id]}
                          onChange={(event) => {
                            setRowSelection((prevRowSelection) => {
                              const res = {
                                ...prevRowSelection,
                                ...(row.original.id
                                  ? { [row.original.id]: event.target.checked }
                                  : { [row.id]: event.target.checked }),
                              };

                              getAllCheckedData &&
                                sendupdatedDataOnSelectOne(res);
                              return res;
                            });
                            !getAllCheckedData &&
                              sendOriginalRow(
                                row.original.id,
                                event.target.checked
                              );
                          }}
                        />
                      </Td>
                    )}
                    {row
                      .getVisibleCells()
                      .map((cell: any, cellIndex: number) => {
                        const { meta }: TObject = cell.column.columnDef;
                        const isLastColumn =
                          cellIndex === row.getVisibleCells().length - 1;

                        if (row.depth === 0) {
                          return (
                            <Td
                              minHeight="52px"
                              key={cell.id}
                              isNumeric={meta?.isNumeric}
                              px={"24px"}
                              fontSize="16px"
                              fontWeight={400}
                              textAlign={
                                centeredCols.includes(cell.column.id)
                                  ? "center"
                                  : "inherit"
                              }
                              maxW="200px"
                              whiteSpace="normal"
                              wordBreak="break-word"
                              overflowWrap="break-word"
                              onClick={
                                isLastColumn
                                  ? (e) => e.stopPropagation()
                                  : undefined
                              }
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </Td>
                          );
                        }
                      })}
                  </Tr>
                  {row.getIsExpanded() && renderSubComponent && (
                    <Tr key={row.id}>
                      <Td colSpan={row.getVisibleCells().length}>
                        {renderSubComponent({ row })}
                      </Td>
                    </Tr>
                  )}
                </Fragment>
              ))
            ) : (
              <Tr>
                <Td colSpan={columns.length} textAlign="center" p="10">
                  <NoRecordsText mt={"0px"} />
                </Td>
              </Tr>
            )}

            {showFooter
              ? table.getFooterGroups().map((footerGroup) => (
                  <Tr key={footerGroup.id}>
                    {footerGroup.headers.map((header) => (
                      <Td
                        minHeight="52px"
                        key={header.id}
                        p="3"
                        fontSize="12px"
                        fontWeight={700}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.footer,
                              header.getContext()
                            )}
                      </Td>
                    ))}
                  </Tr>
                ))
              : null}
          </Tbody>
        </Table>
      </TableContainer>
      {showPagination ? (
        <HStack justifyContent="center" alignItems="center">
          <Paginate
            page={page}
            count={pageCount}
            pageSize={pageSize}
            onPageChange={(page: number) => {
              handlePageChange(page);
            }}
            margin={1}
            variant="ghost"
            isRtl={isRtl}
          />
        </HStack>
      ) : null}
    </Box>
  );
}
